import { create } from 'zustand';
import { registerUserScopedReset, withSession } from './session-read';
import api from '../services/api';
import { useAuthStore } from './auth.store';
import type { Achievement, AchievementStats } from '../types';

// ============================================
// Type Definitions
// ============================================

interface CompletionStats {
  completed: number;
  total: number;
}

interface SummaryData {
  daily: { completed: number; total: number; period_key: string };
  weekly: { completed: number; total: number; period_key: string };
  lifetime: { completed: number; total: number };
  total_points: number;
}

interface CategoryAchievement {
  id: number;
  slug: string;
  name: string;
  description: string;
  points: number;
  icon: string;
  rarity: string;
  threshold: number;
  progress: number;
  requirement_count: number;
  completed: boolean;
  completed_at: string | null;
}

interface AchievementsState {
  isLoading: boolean;
  daily: Achievement[];
  weekly: Achievement[];
  allTime: Achievement[];
  stats: AchievementStats | null;
  recentAchievements: Achievement[];
  hasNewAchievements: boolean;
  lastFetchTime: number | null;
  /** Lightweight summary counts (optimized) */
  summary: SummaryData | null;
  summaryFetchTime: number | null;
  /** Cached category tier details */
  categoryCache: Record<string, CategoryAchievement[]>;
  /** DEV ONLY: When true, skip API fetches to preserve injected mock data */
  _devBypassFetch: boolean;
}

interface AchievementsActions {
  setIsLoading: (isLoading: boolean) => void;
  fetchAll: () => Promise<void>;
  fetchSummary: () => Promise<SummaryData | null>;
  fetchCategory: (category: string) => Promise<CategoryAchievement[]>;
  fetchDaily: () => Promise<void>;
  fetchWeekly: () => Promise<void>;
  fetchAllTime: () => Promise<void>;
  fetchRecent: () => Promise<Achievement[]>;
  trackShare: () => Promise<void>;
  clearRecent: () => void;
  /** Drop all user-scoped achievement data (logout / entering guest mode). */
  reset: () => void;
  invalidateCache: () => void;
  /** Mark a specific achievement as completed (used for cascade animation) */
  markCompleted: (id: number) => void;
  getCompletionStats: () => {
    daily: CompletionStats;
    weekly: CompletionStats;
    allTime: CompletionStats;
  };
  isPerfectDay: () => boolean;
  isPerfectWeek: () => boolean;
}

// Cache durations
const SUMMARY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

type AchievementsStore = AchievementsState & AchievementsActions;

// ============================================
// Store Implementation
// ============================================

export const useAchievementsStore = create<AchievementsStore>((set, get) => ({
  isLoading: false,
  daily: [],
  weekly: [],
  allTime: [],
  stats: null,
  recentAchievements: [],
  hasNewAchievements: false,
  lastFetchTime: null,
  summary: null,
  summaryFetchTime: null,
  categoryCache: {},
  _devBypassFetch: false,

  setIsLoading: (isLoading: boolean) => set({ isLoading }),

  /**
   * Fetch lightweight summary counts (optimized - no full achievement data)
   * Returns cached data if still fresh
   */
  fetchSummary: async (): Promise<SummaryData | null> => {
    const { summary, summaryFetchTime } = get();

    // Return cached summary if still fresh
    if (summary && summaryFetchTime && Date.now() - summaryFetchTime < SUMMARY_CACHE_DURATION) {
      return summary;
    }

    // Session-bound reads (backlog c-030 sweep): an answer for the previous
    // account writes nothing and tells its caller nothing.
    const outcome = await withSession(() => api.get('/achievements/summary'));
    if (outcome.status === 'ended') return null;
    try {
      if (outcome.status === 'failed') throw outcome.error;
      const data: SummaryData = outcome.value.data;
      set({
        summary: data,
        summaryFetchTime: Date.now(),
      });
      return data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch summary:', error);
      }
      return null;
    }
  },

  /**
   * Fetch tier details for a specific category (lazy-loaded)
   * Returns cached data if available
   */
  fetchCategory: async (category: string): Promise<CategoryAchievement[]> => {
    const { categoryCache } = get();

    // Return cached data if available
    if (categoryCache[category]) {
      return categoryCache[category];
    }

    const outcome = await withSession(() => api.get(`/achievements/category/${category}`));
    if (outcome.status === 'ended') return [];
    try {
      if (outcome.status === 'failed') throw outcome.error;
      const achievements = outcome.value.data.achievements || [];

      set({
        categoryCache: {
          ...get().categoryCache,
          [category]: achievements,
        },
      });
      return achievements;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch category:', error);
      }
      return [];
    }
  },

  /**
   * Invalidate all caches (call after achievement unlock)
   */
  invalidateCache: () => {
    set({
      summary: null,
      summaryFetchTime: null,
      categoryCache: {},
      lastFetchTime: null,
    });
  },

  /**
   * Fetch all achievement progress
   */
  fetchAll: async () => {
    // DEV: Skip fetch if dev bypass is active (mock data injected)
    if (import.meta.env.DEV && get()._devBypassFetch) {
      return;
    }

    set({ isLoading: true });
    // Guests get the same-shaped catalog with fresh-user zeros from the public
    // preview endpoint — the Goals tab renders exactly what a new user sees.
    const url = useAuthStore.getState().isGuest ? '/preview/achievements' : '/achievements/me';
    const outcome = await withSession(() => api.get(url));
    if (outcome.status === 'ended') return;
    try {
      if (outcome.status === 'failed') throw outcome.error;
      const data = outcome.value.data;
      set({
        daily: data.daily || [],
        weekly: data.weekly || [],
        allTime: data.all_time || [],
        stats: data.stats || null,
        isLoading: false,
        lastFetchTime: Date.now(),
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch achievements:', error);
      }
      set({ isLoading: false });
    }
  },

  /**
   * Fetch all-time achievements only (lazy-loaded when user views details)
   */
  fetchAllTime: async () => {
    const outcome = await withSession(() => api.get('/achievements/me/all-time'));
    if (outcome.status === 'ended') return;
    try {
      if (outcome.status === 'failed') throw outcome.error;
      set({ allTime: outcome.value.data.achievements || [] });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch all-time achievements:', error);
      }
    }
  },

  /**
   * Fetch daily achievements only
   */
  fetchDaily: async () => {
    const outcome = await withSession(() => api.get('/achievements/me/daily'));
    if (outcome.status === 'ended') return;
    try {
      if (outcome.status === 'failed') throw outcome.error;
      set({ daily: outcome.value.data.achievements || [] });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch daily achievements:', error);
      }
    }
  },

  /**
   * Fetch weekly achievements only
   */
  fetchWeekly: async () => {
    const outcome = await withSession(() => api.get('/achievements/me/weekly'));
    if (outcome.status === 'ended') return;
    try {
      if (outcome.status === 'failed') throw outcome.error;
      set({ weekly: outcome.value.data.achievements || [] });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch weekly achievements:', error);
      }
    }
  },

  /**
   * Fetch recent achievements (for page load animation)
   * In DEV mode, if hasNewAchievements is already true with data, skip API call
   * to support dev testing buttons that inject mock achievements.
   */
  fetchRecent: async (): Promise<Achievement[]> => {
    // DEV: If achievements were injected via dev buttons, use them directly
    const { recentAchievements, hasNewAchievements } = get();
    if (import.meta.env.DEV && hasNewAchievements && recentAchievements.length > 0) {
      return recentAchievements;
    }

    const outcome = await withSession(() => api.get('/achievements/me/recent'));
    if (outcome.status === 'ended') return [];
    try {
      if (outcome.status === 'failed') throw outcome.error;
      const data = outcome.value.data;
      set({
        recentAchievements: data.achievements || [],
        hasNewAchievements: data.has_new || false,
      });
      return data.achievements || [];
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to fetch recent achievements:', error);
      }
      return [];
    }
  },

  /**
   * Track a share action
   */
  trackShare: async () => {
    const outcome = await withSession(() => api.post('/achievements/share'));
    if (outcome.status === 'ended') return;
    try {
      if (outcome.status === 'failed') throw outcome.error;

      // Invalidate cache and refresh summary
      get().invalidateCache();
      void get().fetchSummary();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[AchievementsStore] Failed to track share:', error);
      }
    }
  },

  /**
   * Clear recent achievements (after showing animation)
   */
  clearRecent: () =>
    set({ recentAchievements: [], hasNewAchievements: false, _devBypassFetch: false }),

  reset: () =>
    set({
      isLoading: false,
      daily: [],
      weekly: [],
      allTime: [],
      stats: null,
      recentAchievements: [],
      hasNewAchievements: false,
      lastFetchTime: null,
      summary: null,
      summaryFetchTime: null,
      categoryCache: {},
    }),

  /**
   * Mark a specific achievement as completed (used for cascade animation on profile page)
   */
  markCompleted: (id: number) => {
    const { daily, weekly, allTime } = get();

    // Helper to mark completed in an array
    const markInArray = (arr: Achievement[]): Achievement[] =>
      arr.map((a) => (a.id === id ? { ...a, completed: true } : a));

    set({
      daily: markInArray(daily),
      weekly: markInArray(weekly),
      allTime: markInArray(allTime),
    });
  },

  /**
   * Get completion stats
   * Uses summary data if available (optimized), falls back to counting arrays
   */
  getCompletionStats: () => {
    const { daily, weekly, allTime, summary } = get();

    // Use summary if available (optimized path)
    if (summary) {
      return {
        daily: {
          completed: summary.daily.completed,
          total: summary.daily.total,
        },
        weekly: {
          completed: summary.weekly.completed,
          total: summary.weekly.total,
        },
        allTime: {
          completed: summary.lifetime.completed,
          total: summary.lifetime.total,
        },
      };
    }

    // Fallback to counting arrays
    return {
      daily: {
        completed: daily.filter((a) => a.completed).length,
        total: daily.length,
      },
      weekly: {
        completed: weekly.filter((a) => a.completed).length,
        total: weekly.length,
      },
      allTime: {
        completed: allTime.filter((a) => a.completed).length,
        total: allTime.length,
      },
    };
  },

  /**
   * Check if perfect day is achieved
   */
  isPerfectDay: () => {
    const { daily } = get();
    const perfectDay = daily.find((a) => a.slug === 'daily_perfect');
    return perfectDay?.completed || false;
  },

  /**
   * Check if perfect week is achieved
   */
  isPerfectWeek: () => {
    const { weekly } = get();
    const perfectWeek = weekly.find((a) => a.slug === 'weekly_perfect');
    return perfectWeek?.completed || false;
  },
}));

// Per-user: reset synchronously on every session change and on entering the guest tour (#940 r9).
registerUserScopedReset(() => useAchievementsStore.getState().reset());
